import { Form } from "react-bootstrap";
import type { Clause, ComparisonOperator } from "../../../_services/RulesService";

interface Props {
  clause: Clause;
  onChange: (next: Clause) => void;
}

const OPS: ComparisonOperator[] = [">=", ">", "=", "<=", "<", "!="];

// Extract the leaf-clause discriminator values only — `Clause["type"]` fails
// because composite variants ({ op: 'and' | 'or' | 'not' }) have no `type`.
type LeafClause = Extract<Clause, { type: string }>;
type LeafClauseType = LeafClause["type"];

const TYPE_LABELS: Record<LeafClauseType, string> = {
  scope_completed: "Scope completed",
  attendance_percent: "Attendance percent",
  test_score: "Test score",
  login_streak: "Login streak",
  enrollment_age: "Enrollment age (days)",
  submission_count: "Submission count",
  badge_held: "Holds another badge",
};

const ALL_LEAF_TYPES: LeafClauseType[] = [
  "scope_completed",
  "attendance_percent",
  "test_score",
  "login_streak",
  "enrollment_age",
  "submission_count",
  "badge_held",
];

/** Defaults used when admin changes a clause's type — must satisfy each variant's required fields. */
export function defaultClauseForType(type: LeafClauseType): Clause {
  switch (type) {
    case "scope_completed":
      return { type: "scope_completed", scopeType: "course", scopeId: 0 };
    case "attendance_percent":
      return { type: "attendance_percent", operator: ">=", value: 95, scopeType: "course", scopeId: 0 };
    case "test_score":
      return { type: "test_score", operator: ">=", value: 80, testId: 0 };
    case "login_streak":
      return { type: "login_streak", minDays: 5 };
    case "enrollment_age":
      return { type: "enrollment_age", operator: ">=", valueDays: 365 };
    case "submission_count":
      return { type: "submission_count", scopeType: "course", scopeId: 0, operator: ">=", value: 10 };
    case "badge_held":
      return { type: "badge_held", badgeDefinitionId: 0 };
  }
  return { type: "scope_completed", scopeType: "course", scopeId: 0 };
}

/**
 * Leaf-clause editor. Admin picks the clause type from a dropdown, then
 * the form switches to that variant's fields. Each variant's required
 * fields are typed; defaultClauseForType keeps the JSON valid as the
 * admin toggles types.
 */
const ClauseForm: React.FC<Props> = ({ clause, onChange }) => {
  if ("op" in clause) {
    // Shouldn't happen — PredicateBuilder routes composite clauses elsewhere.
    return <div style={{ color: "var(--status-danger)", fontSize: 12 }}>Composite clause routed to leaf form</div>;
  }
  const type = clause.type;

  const update = (patch: Partial<Clause>) =>
    onChange({ ...(clause as any), ...patch } as Clause);

  return (
    <div className="bm-clause-form">
      <div className="bm-clause-form-row">
        <Form.Label>Clause type</Form.Label>
        <Form.Select
          size="sm"
          value={type}
          onChange={(e) => onChange(defaultClauseForType(e.target.value as LeafClauseType))}
        >
          {ALL_LEAF_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t] || t}
            </option>
          ))}
        </Form.Select>
      </div>

      {type === "scope_completed" && (
        <ScopeFields
          scopeType={(clause as any).scopeType}
          scopeId={(clause as any).scopeId}
          onChange={(scopeType, scopeId) => update({ scopeType, scopeId } as any)}
          allowedTypes={["lesson_plan", "course", "program"]}
        />
      )}

      {type === "attendance_percent" && (
        <>
          <ScopeFields
            scopeType={(clause as any).scopeType}
            scopeId={(clause as any).scopeId}
            onChange={(scopeType, scopeId) => update({ scopeType, scopeId } as any)}
            allowedTypes={["course", "batch"]}
          />
          <OperatorValueFields
            operator={(clause as any).operator}
            value={(clause as any).value}
            valueLabel="Percent"
            onChange={(operator, value) => update({ operator, value } as any)}
          />
        </>
      )}

      {type === "test_score" && (
        <>
          <NumberField
            label="Test ID"
            value={(clause as any).testId}
            onChange={(testId) => update({ testId } as any)}
            placeholder="e.g. 7"
          />
          <OperatorValueFields
            operator={(clause as any).operator}
            value={(clause as any).value}
            valueLabel="Marks"
            onChange={(operator, value) => update({ operator, value } as any)}
          />
        </>
      )}

      {type === "login_streak" && (
        <NumberField
          label="Minimum streak (days)"
          value={(clause as any).minDays}
          onChange={(minDays) => update({ minDays } as any)}
          placeholder="e.g. 5"
        />
      )}

      {type === "enrollment_age" && (
        <OperatorValueFields
          operator={(clause as any).operator}
          value={(clause as any).valueDays}
          valueLabel="Days since enrolled"
          onChange={(operator, valueDays) => update({ operator, valueDays } as any)}
        />
      )}

      {type === "submission_count" && (
        <>
          <ScopeFields
            scopeType={(clause as any).scopeType}
            scopeId={(clause as any).scopeId}
            onChange={(scopeType, scopeId) => update({ scopeType, scopeId } as any)}
            allowedTypes={["lesson_plan", "course", "program"]}
          />
          <OperatorValueFields
            operator={(clause as any).operator}
            value={(clause as any).value}
            valueLabel="Submission count"
            onChange={(operator, value) => update({ operator, value } as any)}
          />
        </>
      )}

      {type === "badge_held" && (
        <NumberField
          label="Badge Definition ID"
          value={(clause as any).badgeDefinitionId}
          onChange={(badgeDefinitionId) => update({ badgeDefinitionId } as any)}
          placeholder="e.g. 12"
        />
      )}
    </div>
  );
};

const ScopeFields: React.FC<{
  scopeType: string;
  scopeId: number;
  allowedTypes: string[];
  onChange: (scopeType: any, scopeId: number) => void;
}> = ({ scopeType, scopeId, allowedTypes, onChange }) => (
  <div className="bm-clause-form-row two-col">
    <div>
      <Form.Label>Scope type</Form.Label>
      <Form.Select
        size="sm"
        value={scopeType}
        onChange={(e) => onChange(e.target.value, scopeId)}
      >
        {allowedTypes.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </Form.Select>
    </div>
    <div>
      <Form.Label>Scope ID</Form.Label>
      <Form.Control
        size="sm"
        type="number"
        value={scopeId || ""}
        onChange={(e) => onChange(scopeType, Number(e.target.value) || 0)}
        placeholder="e.g. 42"
      />
    </div>
  </div>
);

const OperatorValueFields: React.FC<{
  operator: ComparisonOperator;
  value: number;
  valueLabel: string;
  onChange: (operator: ComparisonOperator, value: number) => void;
}> = ({ operator, value, valueLabel, onChange }) => (
  <div className="bm-clause-form-row two-col">
    <div>
      <Form.Label>Operator</Form.Label>
      <Form.Select
        size="sm"
        value={operator}
        onChange={(e) => onChange(e.target.value as ComparisonOperator, value)}
      >
        {OPS.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </Form.Select>
    </div>
    <div>
      <Form.Label>{valueLabel}</Form.Label>
      <Form.Control
        size="sm"
        type="number"
        value={value}
        onChange={(e) => onChange(operator, Number(e.target.value))}
      />
    </div>
  </div>
);

const NumberField: React.FC<{
  label: string;
  value: number;
  placeholder?: string;
  onChange: (v: number) => void;
}> = ({ label, value, placeholder, onChange }) => (
  <div className="bm-clause-form-row">
    <Form.Label>{label}</Form.Label>
    <Form.Control
      size="sm"
      type="number"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
    />
  </div>
);

export default ClauseForm;
